/**************************************************************************************************************************************************
*   MODULE NAME     :  MasterDataManagement
*   AUTHOR          :  GLSD DEV Team
*   DATE            :  22-04-2021
*   DESCRIPTION     :  Component to load Masterdata Management page
*   Copyright 2021 Eli Lilly and Company
*   All rights reserved
*
/**************************************************************************************************************************************************
*   *** CHANGE HISTORY ***
/**************************************************************************************************************************************************
*   TRACKING NUMBER: CHG1689267
*   DESCRIPTION: <GLSD>: New System Development
/**************************************************************************************************************************************************/

import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { bindActionCreators } from "redux";
import swal from "sweetalert";
import {FaInfoCircle, FaSpinner} from "react-icons/fa"
import { Row, Col, Button,Form, OverlayTrigger, Tooltip} from 'react-bootstrap';
import MasterDataApi from "../../api/MasterDataApi";
import { loadAlertsEmailsMasterData, saveMasterData} from "../../actions/masterdataActions";
import * as MessagePool from '../common/MessagesPool';


const MasterDataManagement = (props) => {
    const masterDataMapper = {
        "GPS_LIT_COM_MAILBOX": "globalMailbox",
        "GPS_JP_COM_MAILBOX": "japanMailbox",
        "GPS_GLOBAL_UTILITY_MAILBOX": "globalUtilityMailbox",
        "GPS_JAPAN_UTILITY_MAILBOX": "japanUtilityMailbox",
        "ALERT_DAYS_ABST_NOT_REVD": "reviewingAbstract",
        "ALERT_DAYS_TRANS_NOT_RET": "returningTranslation",
        "ALERT_DAYS_ART_NOT_RECVD": "receivingArticle",
        "ALERT_DAYS_ART_NOT_REVD": "reviewingArticle"
    }
    const [masterData, setMasterData] = useState({
        reviewingArticle:'',
        returningTranslation: '',
        receivingArticle: '',
        reviewingAbstract: '',
        globalMailbox:'',
        japanMailbox:'',
        globalUtilityMailbox:'',
        japanUtilityMailbox:''
    });
    const [validationCheck, setValidationCheck] = useState({
        globalMailbox: false,
        japanMailbox: false,
        globalUtilityMailbox: false,
        japanUtilityMailbox: false
    });
    const [orgMasterData, setOrgMasterData] = useState([]);
    const [isSaveEnabled, setSave] = useState(true);
    const [isLoading, setLoader] = useState(false);
    const {reviewingArticle,returningTranslation,receivingArticle,reviewingAbstract,
         globalMailbox,japanMailbox,japanUtilityMailbox,globalUtilityMailbox} = masterData;
    /****  
     * useEffect hook : Similar to componentDidMount and componentDidUpdate
     ****/
    useEffect(() => {
        getMasterData();
    }, []);

    const checkEmailValidation = (str) => {
        function validateEmail(email) {
            var re = /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i;
            return re.test(email);
        }
        if(str.trim() === '') return true;
        var emails = str.trim().split(';').map(e => e.trim()).filter(e => e !== '');
        if(emails.length < str.trim().split(';').length) return false;
        return emails.length > 0 && !emails.some(email => !validateEmail(email));
    }  

    const handleChange = (e) => {
        if(e.target.type !== "number") {
            let validation = !checkEmailValidation(e.target.value);
            setValidationCheck({
                ...validationCheck,
                [e.target.name]: validation
            });
            setSave(!(Object.values({
                ...validationCheck,
                [e.target.name]: validation
            }).some(e => e)))
        }
        setMasterData({
            ...masterData,
            [e.target.name]: e.target.type === "number" ? Math.max(0, Math.min(365, +e.target.value)) : e.target.value.substr(0, 250)
        });
    }

    const getMasterData = async () => {
        setLoader(true);
        try {
            let res = await props.loadAlertsEmailsMasterData();
            if(res && res.data && res.data.result) {
                let data = res.data.result;
                setOrgMasterData(data);
                let masterDataValue = {};
                data.forEach(e => {
                    masterDataValue[masterDataMapper[e.masterDataKey]] = e.masterDataValue;
                });
                setMasterData({
                    ...masterData,
                    ...masterDataValue
                })
            }
            setLoader(false);
        } catch (e) {
            setLoader(false);
        }
    }

    const handleSaveFunction = async () =>{
        setLoader(true);
        let revMasterMapper = {};
        Object.keys(masterDataMapper).map(key => {
            revMasterMapper[masterDataMapper[key]] = key;
        });
        let saveData = [];
        Object.keys(masterData).forEach(key => {
            let row = {...orgMasterData.find(e => e.masterDataKey === revMasterMapper[key])};
            saveData.push({...row, masterDataValue: masterData[key]});
        });
        let res = await props.saveMasterData(saveData);
        if(res && res.data && res.data.result) {
            swal("Success", MessagePool.RECORDS_UPDATE_SUCCESS, "success").then(() => {
                getMasterData();
            });
        } else {
            setLoader(false);
            swal('Oops !!', MessagePool.SERVER_ERROR, 'error')
        }
    }

    return (
        <>
        {isLoading && (
          <div className="loader">
            <FaSpinner />
          </div>
        )}
        <div className="masterDataWrap">
            <div className="alertInterval">
                <h4>Alert Interval</h4>
                <hr/>
                <Row className="pl-3 pr-3">
                    <Col sm="6">
                        <Form.Group controlId="alertInterval1">
                            <Form.Label>Number of days for reviewing an abstract after receipt:</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="reviewingAbstract"
                                    value={ reviewingAbstract}
                                    onChange={handleChange}
                                  />
                        </Form.Group>
                    </Col>
 
                    <Col sm="6">
                        <Form.Group controlId="alertInterval2">
                            <Form.Label>Number of days for returning a translation request:</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="returningTranslation"
                                    value={returningTranslation}
                                    onChange={handleChange}
                                  />
                        </Form.Group>
                    </Col>
                    <Col sm="6">
                        <Form.Group controlId="alertInterval3">
                            <Form.Label>Number of days for receiving an article after initial order:</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="receivingArticle"
                                    value={receivingArticle}
                                    onChange={handleChange}
                                  />
                        </Form.Group>
                    </Col>
                    <Col sm="6">
                        <Form.Group controlId="alertInterval4">
                            <Form.Label>Number of days for reviewing an article after receipt:</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="reviewingArticle"
                                    value={reviewingArticle}
                                    onChange={handleChange}
                                  />
                        </Form.Group>
                    </Col>
                </Row>
            </div>
            <div className="mailboxform">
                <h4>Mailbox </h4>
                <i style={{color: 'red'}}>Please use semicolons(;) for multiple emails.(ex: example@lilly.com;example@network.lilly.com)</i>
                <hr/>
                <Row className="pl-3 pr-3">
                    <Col sm="6">
                        <Form.Group controlId="mailboxform1">
                            <Form.Label>GLSD Global Mailbox</Form.Label>
                                <Form.Control
                                   as="textarea" rows="2"
                                    name="globalMailbox"
                                    value={globalMailbox}
                                    onChange={handleChange}
                                    isInvalid={validationCheck.globalMailbox}
                                  />
                                <Form.Control.Feedback type="invalid">
                                    Invalid Format.
                                </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
 
                    <Col sm="6">
                        <Form.Group controlId="mailboxform2">
                            <Form.Label>GLSD Japan Mailbox</Form.Label>
                                <Form.Control
                                    as="textarea" rows="2"
                                    name="japanMailbox"
                                    value={japanMailbox}
                                    onChange={handleChange}
                                    isInvalid={validationCheck.japanMailbox}
                                    />
                                  <Form.Control.Feedback type="invalid">
                                      Invalid Format.
                                  </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col sm="6">
                        <Form.Group controlId="mailboxform3">
                            <Form.Label>GLSD Global Utility Mailbox</Form.Label>
                                <Form.Control
                                   as="textarea" rows="2"
                                    name="globalUtilityMailbox"
                                    value={globalUtilityMailbox}
                                    onChange={handleChange}
                                    isInvalid={validationCheck.globalUtilityMailbox}
                                    />
                                  <Form.Control.Feedback type="invalid">
                                      Invalid Format.
                                  </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col sm="6">
                        <Form.Group controlId="mailboxform4">
                            <Form.Label>GLSD Japan Utility Mailbox</Form.Label>
                                <Form.Control
                                    as="textarea" rows="2"
                                    name="japanUtilityMailbox"
                                    value={japanUtilityMailbox}
                                    onChange={handleChange}
                                    isInvalid={validationCheck.japanUtilityMailbox}
                                    />
                                  <Form.Control.Feedback type="invalid">
                                      Invalid Format.
                                  </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

            </div>

            <div className="text-right pt-4 pb-2 pr-4">
            <Button variant="primary" type="submit" disabled={!isSaveEnabled} onClick={handleSaveFunction} >
                                        Save
                            </Button>
            </div>
        </div>
        </>
    );
}

const mapStateToProps = state => {
    return {
    };
};


const mapDispatchToProps = dispatch => {
    return bindActionCreators(
        {
            loadAlertsEmailsMasterData, 
            saveMasterData
        },
        dispatch
    );
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MasterDataManagement));
